import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Book, ChevronLeft, ChevronRight, Sparkles, Leaf, Cloud, Mountain, Heart } from 'lucide-react';
import { Button } from '../../../components/ui/button.jsx';
import { sampleStory } from '../data/sampleStory.js';

// Page-specific placeholder illustrations
const pageIllustrations = {
  cover: {
    gradient: 'from-[#7B68EE] via-[#9B8AC4] to-[#8B9FD9]',
    icon: Book,
  },
  1: {
    gradient: 'from-[#7B68EE] to-[#9B8AC4]',
    icon: Mountain,
  },
  2: {
    gradient: 'from-[#9B8AC4] to-[#D4A5C9]',
    icon: Heart,
  },
  3: {
    gradient: 'from-[#7EBDC3] to-[#8B9FD9]',
    icon: Cloud,
  },
  4: {
    gradient: 'from-[#8B9FD9] to-[#7B68EE]',
    icon: Sparkles,
  },
  5: {
    gradient: 'from-[#7DB896] to-[#7EBDC3]',
    icon: Mountain,
  },
};

// Beautiful SVG-based placeholder illustration
function StoryIllustration({ pageNum, title, className = "" }) {
  const config = pageIllustrations[pageNum] || pageIllustrations[1];
  const IconComponent = config.icon;

  return (
    <div className={`w-full h-full bg-gradient-to-br ${config.gradient} flex items-center justify-center relative overflow-hidden ${className}`}>
      {/* Background dots pattern */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id={`dots-${pageNum}`} patternUnits="userSpaceOnUse" width="10" height="10">
              <circle cx="5" cy="5" r="1" fill="white" />
            </pattern>
          </defs>
          <rect width="100" height="100" fill={`url(#dots-${pageNum})`} />
        </svg>
      </div>

      {/* Floating decorative elements */}
      <motion.div
        className="absolute top-4 right-4"
        animate={{ y: [0, -8, 0], rotate: [0, 10, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      >
        <Sparkles className="w-6 h-6 text-white/40" />
      </motion.div>
      <motion.div
        className="absolute bottom-6 left-6"
        animate={{ y: [0, 6, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
      >
        <Leaf className="w-5 h-5 text-white/30" />
      </motion.div>

      {/* Main content */}
      <div className="text-center p-6 relative z-10">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="mb-4"
        >
          <div className="w-20 h-20 mx-auto bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30">
            <IconComponent className="w-10 h-10 text-white" />
          </div>
        </motion.div>
        <motion.p
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-white/90 text-sm font-medium"
        >
          {pageNum === 'cover' ? title : `Page ${pageNum}`}
        </motion.p>
        <motion.p
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-white/60 text-xs mt-1"
        >
          AI illustration preview
        </motion.p>
      </div>
    </div>
  );
}

/**
 * LandingStorybookPreview - Interactive story as a realistic open book
 */
export default function LandingStorybookPreview({ onJoinWaitlist }) {
  const [currentPage, setCurrentPage] = useState(0); // 0 = cover
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipDirection, setFlipDirection] = useState(1); // 1 = forward, -1 = backward
  const [touchStart, setTouchStart] = useState(null);

  const { title, pages } = sampleStory;
  const totalPages = pages.length + 1;
  const isOnCover = currentPage === 0;
  const isOnLastPage = currentPage === totalPages - 1;

  const nextPage = () => {
    if (currentPage < totalPages - 1 && !isFlipping) {
      setFlipDirection(1);
      setIsFlipping(true);
    }
  };

  const prevPage = () => {
    if (currentPage > 0 && !isFlipping) {
      setFlipDirection(-1);
      setIsFlipping(true);
    }
  };

  const handleFlipComplete = () => {
    setCurrentPage(prev => prev + flipDirection);
    setIsFlipping(false);
  };

  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextPage();
      else prevPage();
    }
    setTouchStart(null);
  };

  const currentPageData = isOnCover ? null : pages[currentPage - 1];
  const nextPageData = currentPage < pages.length ? pages[currentPage] : null;
  const prevPageData = currentPage > 1 ? pages[currentPage - 2] : null;

  return (
    <section id="story-preview" className="py-16 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #F5EBF3 0%, #F8F6FB 100%)' }}>
      {/* Decorative background shapes */}
      <motion.div
        className="absolute top-10 right-10 w-32 h-32 rounded-full bg-[#7B68EE]/10"
        animate={{ scale: [1, 1.1, 1], rotate: [0, 5, 0] }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-20 left-10 w-24 h-24 rounded-full bg-[#9B8AC4]/10"
        animate={{ y: [0, -15, 0] }}
        transition={{ duration: 6, repeat: Infinity, delay: 1 }}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="section-header mb-4">Experience a Story</h2>
          <p className="text-[#5C534A] text-lg max-w-2xl mx-auto">
            Flip through a sample story. Every page features AI-generated illustrations personalized for your child.
          </p>
        </motion.div>

        {/* Open Book Container - Fixed positioning wrapper for arrows */}
        <div className="relative mx-auto" style={{ maxWidth: '900px' }}>
          {/* Navigation Arrows - Outside the 3D transform context */}
          {/* Wrapper divs handle positioning so motion animations don't interfere */}
          {currentPage > 0 && (
            <div className="absolute -left-4 sm:-left-12 top-1/2 -translate-y-1/2 z-50">
              <motion.button
                onClick={prevPage}
                disabled={isFlipping}
                aria-label="Previous sample story pages"
                className="p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-shadow border border-[#E8DFD5] disabled:opacity-50"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <ChevronLeft className="w-5 h-5 text-[#5C534A]" />
              </motion.button>
            </div>
          )}
          {currentPage < totalPages - 1 && (
            <div className="absolute -right-4 sm:-right-12 top-1/2 -translate-y-1/2 z-50">
              <motion.button
                onClick={nextPage}
                disabled={isFlipping}
                aria-label="Next sample story pages"
                className="p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-shadow border border-[#E8DFD5] disabled:opacity-50"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <ChevronRight className="w-5 h-5 text-[#5C534A]" />
              </motion.button>
            </div>
          )}

          {/* The Book with 3D perspective */}
          <motion.div
            className="relative"
            style={{ perspective: '2500px' }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
          {/* Book shadow on surface */}
          <div
            className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-[90%] h-12 rounded-[50%]"
            style={{
              background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.25) 0%, transparent 70%)',
            }}
          />

          {/* The Open Book */}
          <div
            className="relative flex"
            style={{
              transform: 'rotateX(5deg)',
              transformStyle: 'preserve-3d',
            }}
          >
            {/* Left Page Stack (visible edge) */}
            <div className="absolute left-0 top-2 bottom-2 w-3 z-0">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="absolute top-0 bottom-0"
                  style={{
                    left: `${i * 2}px`,
                    width: '2px',
                    background: `linear-gradient(180deg, #E8E2D8 0%, #F5F0E8 50%, #E8E2D8 100%)`,
                    boxShadow: i === 5 ? '-1px 0 2px rgba(0,0,0,0.05)' : 'none',
                  }}
                />
              ))}
            </div>

            {/* Left Page - Illustration */}
            <motion.div
              className="relative w-1/2 aspect-[3/4] origin-right"
              style={{
                background: '#FBF8F3',
                borderRadius: '4px 0 0 4px',
                boxShadow: 'inset -8px 0 20px rgba(0,0,0,0.08), -4px 4px 20px rgba(0,0,0,0.15)',
              }}
            >
              {/* Paper texture - cream/off-white with visible grain */}
              <div
                className="absolute inset-0 rounded-l pointer-events-none"
                style={{
                  backgroundImage: `
                    url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")
                  `,
                  opacity: 0.06,
                  mixBlendMode: 'multiply',
                }}
              />

              {/* Subtle fiber lines like real paper */}
              <div
                className="absolute inset-0 rounded-l pointer-events-none"
                style={{
                  backgroundImage: `
                    repeating-linear-gradient(
                      90deg,
                      transparent,
                      transparent 40px,
                      rgba(139, 131, 120, 0.03) 40px,
                      rgba(139, 131, 120, 0.03) 41px
                    ),
                    repeating-linear-gradient(
                      0deg,
                      transparent,
                      transparent 35px,
                      rgba(139, 131, 120, 0.02) 35px,
                      rgba(139, 131, 120, 0.02) 36px
                    )
                  `,
                }}
              />

              {/* Page content - Illustration area with margin */}
              {/* Only changes during backward flip (to previous page), stays current during forward flip */}
              <div className="absolute inset-4 sm:inset-6 rounded overflow-hidden shadow-inner">
                <StoryIllustration
                  pageNum={
                    isFlipping && flipDirection < 0
                      ? currentPage - 1 <= 0 ? 'cover' : currentPage - 1  // Show prev illustration when flipping backward
                      : isOnCover
                      ? 'cover'
                      : currentPage
                  }
                  title={title}
                />
              </div>

              {/* Page number - left page */}
              {/* Only changes during backward flip (to previous page), stays current during forward flip */}
              <div className="absolute bottom-3 left-4 text-xs text-[#A09890] font-serif italic">
                {isFlipping && flipDirection < 0
                  ? (currentPage - 1 <= 0 ? '' : currentPage - 1)
                  : isOnCover ? '' : currentPage}
              </div>

              {/* Worn corner effect */}
              <div
                className="absolute top-0 left-0 w-6 h-6"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,0,0,0.03) 0%, transparent 50%)',
                  borderRadius: '4px 0 0 0',
                }}
              />
            </motion.div>

            {/* Center spine/binding */}
            <div
              className="relative w-4 z-20"
              style={{
                background: 'linear-gradient(90deg, #D4CFC5 0%, #E8E2D8 15%, #F5F0E8 50%, #E8E2D8 85%, #D4CFC5 100%)',
                boxShadow: 'inset 2px 0 4px rgba(0,0,0,0.1), inset -2px 0 4px rgba(0,0,0,0.1)',
              }}
            >
              {/* Binding stitches */}
              <div className="absolute inset-x-0 top-4 bottom-4 flex flex-col justify-around items-center">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1 h-3 rounded-full"
                    style={{ background: 'rgba(0,0,0,0.08)' }}
                  />
                ))}
              </div>
            </div>

            {/* Right Page - Text */}
            <motion.div
              className="relative w-1/2 aspect-[3/4] origin-left"
              style={{
                background: '#FFFCF8',
                borderRadius: '0 4px 4px 0',
                boxShadow: 'inset 8px 0 20px rgba(0,0,0,0.05), 4px 4px 20px rgba(0,0,0,0.15)',
              }}
            >
              {/* Paper texture */}
              <div
                className="absolute inset-0 rounded-r pointer-events-none"
                style={{
                  backgroundImage: `
                    url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise2'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise2)'/%3E%3C/svg%3E")
                  `,
                  opacity: 0.05,
                  mixBlendMode: 'multiply',
                }}
              />

              {/* Fiber lines */}
              <div
                className="absolute inset-0 rounded-r pointer-events-none"
                style={{
                  backgroundImage: `
                    repeating-linear-gradient(
                      90deg,
                      transparent,
                      transparent 45px,
                      rgba(139, 131, 120, 0.025) 45px,
                      rgba(139, 131, 120, 0.025) 46px
                    ),
                    repeating-linear-gradient(
                      0deg,
                      transparent,
                      transparent 38px,
                      rgba(139, 131, 120, 0.018) 38px,
                      rgba(139, 131, 120, 0.018) 39px
                    )
                  `,
                }}
              />

              {/* Text content */}
              {/* When flipping forward, show next text (revealed underneath). When flipping backward, right side stays current. */}
              <div className="absolute inset-4 sm:inset-6 md:inset-8 flex flex-col">
                {isFlipping && flipDirection > 0 ? (
                  /* Show next page text when flipping forward - this is revealed underneath */
                  <div className="flex-1 flex flex-col justify-center">
                    <p className="text-[#3D3832] text-sm sm:text-base md:text-lg leading-relaxed font-serif">
                      {nextPageData?.text}
                    </p>
                  </div>
                ) : isOnCover || (isFlipping && flipDirection < 0 && currentPage - 1 <= 0) ? (
                  /* Cover title page */
                  <div className="flex-1 flex flex-col items-center justify-center text-center">
                    <div className="w-12 h-1 bg-gradient-to-r from-transparent via-[#9B8AC4] to-transparent mb-6" />
                    <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#3D3832] font-display leading-tight mb-4">
                      {title}
                    </h3>
                    <p className="text-[#8B8279] text-sm mb-6">A Mindfulness Story</p>
                    <div className="w-12 h-1 bg-gradient-to-r from-transparent via-[#9B8AC4] to-transparent" />
                  </div>
                ) : (
                  /* Story text */
                  <div className="flex-1 flex flex-col justify-center">
                    <p className="text-[#3D3832] text-sm sm:text-base md:text-lg leading-relaxed font-serif">
                      {currentPageData?.text}
                    </p>
                  </div>
                )}
              </div>

              {/* Page number - right page */}
              {/* Only changes during forward flip (to next page), stays current during backward flip */}
              <div className="absolute bottom-3 right-4 text-xs text-[#A09890] font-serif italic">
                {isFlipping && flipDirection > 0 ? currentPage + 1 : isOnCover ? '' : currentPage}
              </div>

              {/* Subtle page curl in corner */}
              <div
                className="absolute bottom-0 right-0 w-8 h-8 overflow-hidden"
                style={{ borderRadius: '0 0 4px 0' }}
              >
                <div
                  className="absolute bottom-0 right-0 w-12 h-12 origin-bottom-right"
                  style={{
                    background: 'linear-gradient(315deg, #E8E2D8 0%, transparent 60%)',
                    transform: 'rotate(0deg)',
                    boxShadow: '-1px -1px 3px rgba(0,0,0,0.05)',
                  }}
                />
              </div>
            </motion.div>

            {/* Right Page Stack (visible edge) */}
            <div className="absolute right-0 top-2 bottom-2 w-3 z-0">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="absolute top-0 bottom-0"
                  style={{
                    right: `${i * 2}px`,
                    width: '2px',
                    background: `linear-gradient(180deg, #E8E2D8 0%, #F5F0E8 50%, #E8E2D8 100%)`,
                    boxShadow: i === 5 ? '1px 0 2px rgba(0,0,0,0.05)' : 'none',
                  }}
                />
              ))}
            </div>

            {/* Page Turn Animation Overlay */}
            <AnimatePresence mode="wait">
              {isFlipping && (
                <motion.div
                  className="absolute z-40"
                  style={{
                    // Position over right half when going forward, left half when going backward
                    left: flipDirection > 0 ? '50%' : '0',
                    right: flipDirection > 0 ? '0' : '50%',
                    top: 0,
                    bottom: 0,
                    transformStyle: 'preserve-3d',
                    transformOrigin: flipDirection > 0 ? 'left center' : 'right center',
                    willChange: 'transform',
                  }}
                  initial={{ rotateY: 0 }}
                  animate={{ rotateY: flipDirection > 0 ? -180 : 180 }}
                  transition={{
                    duration: 0.8,
                    ease: [0.25, 0.1, 0.25, 1], // Smooth cubic bezier for natural page flip
                  }}
                  onAnimationComplete={handleFlipComplete}
                >
                  {/* Front of turning page */}
                  <motion.div
                    className="absolute inset-0 bg-[#FBF8F3] overflow-hidden"
                    style={{
                      backfaceVisibility: 'hidden',
                      borderRadius: flipDirection > 0 ? '0 4px 4px 0' : '4px 0 0 4px',
                      willChange: 'transform, box-shadow',
                    }}
                    initial={{
                      boxShadow: flipDirection > 0
                        ? '-2px 0 8px rgba(0,0,0,0.08)'
                        : '2px 0 8px rgba(0,0,0,0.08)',
                    }}
                    animate={{
                      boxShadow: flipDirection > 0
                        ? '-20px 0 40px rgba(0,0,0,0.25)'
                        : '20px 0 40px rgba(0,0,0,0.25)',
                    }}
                    transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                  >
                    {/* Paper texture on front */}
                    <div className="absolute inset-0 pointer-events-none" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                      opacity: 0.06,
                      mixBlendMode: 'multiply',
                    }} />
                    {/* Current page content */}
                    <div className="absolute inset-4 sm:inset-6 flex flex-col justify-center">
                      {flipDirection > 0 ? (
                        // Forward: show current text
                        <p className="text-[#3D3832] text-sm sm:text-base leading-relaxed font-serif">
                          {currentPageData?.text || title}
                        </p>
                      ) : (
                        // Backward: show current illustration
                        <div className="w-full h-full rounded overflow-hidden">
                          <StoryIllustration pageNum={currentPage} title={title} />
                        </div>
                      )}
                    </div>
                  </motion.div>

                  {/* Back of turning page (revealed during flip) */}
                  <motion.div
                    className="absolute inset-0 bg-[#FFFCF8] overflow-hidden"
                    style={{
                      backfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)',
                      borderRadius: flipDirection > 0 ? '4px 0 0 4px' : '0 4px 4px 0',
                      willChange: 'transform, box-shadow',
                    }}
                    initial={{
                      boxShadow: flipDirection > 0
                        ? '2px 0 8px rgba(0,0,0,0.08)'
                        : '-2px 0 8px rgba(0,0,0,0.08)',
                    }}
                    animate={{
                      boxShadow: flipDirection > 0
                        ? '10px 0 25px rgba(0,0,0,0.18)'
                        : '-10px 0 25px rgba(0,0,0,0.18)',
                    }}
                    transition={{ duration: 0.4, delay: 0.4, ease: [0.4, 0, 0.2, 1] }}
                  >
                    {/* Paper texture on back */}
                    <div className="absolute inset-0 pointer-events-none" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise2'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise2)'/%3E%3C/svg%3E")`,
                      opacity: 0.05,
                      mixBlendMode: 'multiply',
                    }} />
                    {/* Next/previous page content preview */}
                    <div className="absolute inset-4 sm:inset-6 flex flex-col justify-center">
                      {flipDirection > 0 ? (
                        // Forward: back shows next illustration
                        <div className="w-full h-full rounded overflow-hidden">
                          <StoryIllustration pageNum={currentPage + 1} title={title} />
                        </div>
                      ) : (
                        // Backward: back shows previous text
                        <p className="text-[#3D3832] text-sm sm:text-base leading-relaxed font-serif">
                          {prevPageData?.text || title}
                        </p>
                      )}
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Page indicator */}
          <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 flex items-center gap-2">
            <span className="text-sm text-[#8B8279]">
              {isOnCover ? 'Cover' : `Page ${currentPage}`}
            </span>
            <span className="text-[#C4BBAD]">•</span>
            <span className="text-sm text-[#A09890]">
              {pages.length} pages
            </span>
          </div>
        </motion.div>
        </div>

        {/* CTA on last page */}
        {isOnLastPage && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-16 text-center"
          >
            <p className="text-[#5C534A] mb-4">
              Ready to create personalized stories for your child?
            </p>
            <Button
              onClick={onJoinWaitlist}
              className="bg-gradient-to-r from-[#7B68EE] to-[#9B8AC4] hover:from-[#5B4BC7] hover:to-[#8B7AB8] text-white px-8 py-3 rounded-full shadow-lg transition-transform transform hover:scale-105"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Create Your Own Story
            </Button>
          </motion.div>
        )}
      </div>
    </section>
  );
}
